import {translate} from 'views/module';

import {getParticipantKind, getParticipantName, getParticipantResourceRn, getParticipantRns} from '../communication/communicationUtils';

import moment from 'moment-timezone';

export const OVERDUE_JOB_MINUTES = 5;
export const STUCK_OPEN_MINUTES = 15;
export const NOTHING_SCHEDULED_HOURS = 2;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
// createdAt and updatedAt are written separately when a case is created, so a
// case nobody has touched still has them a few milliseconds apart.
const NO_ACTIVITY_TOLERANCE_MS = 5 * 1000;
// A guard against a paging loop, far above any real result set.
const MAX_QUERY_PAGES = 500;

// metricDate is a Toronto civil day, so the marked-day list is built in Toronto
// and not in whatever zone the reader's browser is in.
const markingTimezone = 'America/Toronto';

export const monitorRanges = [
  {key: 'LAST_7_DAYS', days: 7},
  {key: 'LAST_14_DAYS', days: 14},
  {key: 'LAST_28_DAYS', days: 28},
  {key: 'ALL_TIME'},
];

// Precedence order: a case is listed once, under the first flag it has, and the
// type table counts it the same way so its columns add up to the group sizes.
export const flagKeys = [
  'OVERDUE_JOB',
  'STUCK_OPEN',
  'NOTHING_SCHEDULED',
  'MARKED_BAD',
  'HUMAN_INTERVENTION',
  'FLAGGED',
];

export const stalledFlagKeys = ['OVERDUE_JOB', 'STUCK_OPEN', 'NOTHING_SCHEDULED'];

export const markFlagKeys = ['MARKED_BAD', 'HUMAN_INTERVENTION', 'FLAGGED'];

export const COMPLETED_GROUP_KEY = 'COMPLETED';

// Drops actionLog and domainGuide, the two heavy fields: the first log entry
// carries the whole raw event.
export const caseQueryFields = [
  'caseRn',
  'caseType',
  'status',
  'priority',
  'participants',
  'domainGuideRn',
  'markingStatus',
  'markingGrade',
  'markingFailureCategory',
  'markingMarkerType',
  'markingMetricDate',
  'createdAt',
  'updatedAt',
];

const caseQuerySort = {sortKey: 'createdAt', sortDirection: 'DESC'};

const getTime = (value) => {
  const time = new Date(value).getTime();

  return Number.isNaN(time) ? null : time;
};

export const buildMarkedMetricDates = (days) => {
  return Array.from({length: days}, (unusedValue, dayOffset) => {
    return moment().tz(markingTimezone).subtract(dayOffset, 'days').format('YYYY-MM-DD');
  });
};

export const getRangeStartTime = (range, now) => {
  if (!range?.days) {
    return null;
  }

  return moment(now).subtract(range.days - 1, 'days').startOf('day').valueOf();
};

// Open and pending cases are bounded by their last activity, not by when they
// were created, so an old case that is still moving stays in view. No range
// start means All time.
export const filterActiveSince = (cases, rangeStartTime) => {
  if (rangeStartTime === null) {
    return cases;
  }

  return cases.filter((caseData) => (getTime(caseData.updatedAt || caseData.createdAt) || 0) >= rangeStartTime);
};

// The index holds one document per participant, and the engine dedupes within
// a page only, so the same case comes back on more than one page.
export const mergeCasesByRn = (currentCases, nextCases) => {
  const casesByRn = new Map(currentCases.map((caseData) => [caseData.caseRn, caseData]));

  nextCases.forEach((caseData) => {
    if (!caseData?.caseRn) return;

    casesByRn.set(caseData.caseRn, {
      ...casesByRn.get(caseData.caseRn),
      ...caseData,
    });
  });

  return [...casesByRn.values()];
};

export const loadAllCasePages = async (queryCase, filters, isCurrent, onPage) => {
  let cases = [];
  let pages = 0;
  let lastEvaluatedKey;

  do {
    const response = await queryCase(filters, caseQuerySort, lastEvaluatedKey, caseQueryFields);

    if (!isCurrent()) {
      return null;
    }

    pages += 1;
    cases = mergeCasesByRn(cases, response?.list || []);
    lastEvaluatedKey = response?.lastEvaluatedKey;
    onPage(pages);
  } while (lastEvaluatedKey && pages < MAX_QUERY_PAGES);

  return {cases, pages};
};

// Agent prompt jobs have no case, so they cannot place a flag anywhere.
export const buildJobsByCaseRn = (jobs) => {
  return jobs.reduce((result, job) => {
    if (!job?.caseRn) {
      return result;
    }

    return {
      ...result,
      [job.caseRn]: [...(result[job.caseRn] || []), job],
    };
  }, {});
};

export const countJobsWithoutCase = (jobs) => {
  return jobs.filter((job) => !job?.caseRn).length;
};

export const isJobOverdue = (job, now) => {
  const executeAt = getTime(job?.executeAt);

  return executeAt !== null && executeAt < now - (OVERDUE_JOB_MINUTES * MINUTE_MS);
};

export const getOverdueCaseRns = (jobsByCaseRn, now) => {
  return Object.keys(jobsByCaseRn).filter((caseRn) => {
    return jobsByCaseRn[caseRn].some((job) => isJobOverdue(job, now));
  });
};

const isOpenOrPending = (caseData) => {
  return ['OPEN', 'PENDING'].includes(caseData.status);
};

const getCaseFlags = (caseData, {caseJobs, jobsLoaded, isMarkedInRange, now}) => {
  const updatedAt = getTime(caseData.updatedAt || caseData.createdAt);
  const flags = [];

  // Unknown, not clear: without the job list a case cannot be judged on jobs.
  if (jobsLoaded && caseJobs.some((job) => isJobOverdue(job, now))) {
    flags.push('OVERDUE_JOB');
  }

  if (caseData.status === 'OPEN' && updatedAt !== null && updatedAt < now - (STUCK_OPEN_MINUTES * MINUTE_MS)) {
    flags.push('STUCK_OPEN');
  }

  if (
    jobsLoaded &&
    caseData.status === 'PENDING' &&
    caseJobs.length === 0 &&
    updatedAt !== null &&
    updatedAt < now - (NOTHING_SCHEDULED_HOURS * HOUR_MS)
  ) {
    flags.push('NOTHING_SCHEDULED');
  }

  if (isMarkedInRange) {
    // markingStatus folds UNCERTAIN and SKIPPED_INTERVENTION into FLAGGED, so
    // the split is read from the grade.
    if (caseData.markingGrade === 'FAIL') {
      flags.push('MARKED_BAD');
    } else if (caseData.markingGrade === 'SKIPPED_INTERVENTION') {
      flags.push('HUMAN_INTERVENTION');
    } else if (caseData.markingGrade === 'UNCERTAIN') {
      flags.push('FLAGGED');
    }
  }

  return flags;
};

export const buildMonitorRows = ({cases, markedCaseRns, completedCaseRns, jobsByCaseRn, jobsLoaded, now}) => {
  return cases.map((caseData) => {
    const caseJobs = [...(jobsByCaseRn[caseData.caseRn] || [])].sort((firstJob, secondJob) => {
      return (getTime(firstJob.executeAt) || 0) - (getTime(secondJob.executeAt) || 0);
    });
    const isMarkedInRange = markedCaseRns.has(caseData.caseRn);
    const flags = getCaseFlags(caseData, {caseJobs, jobsLoaded, isMarkedInRange, now});

    return {
      ...caseData,
      flags,
      primaryFlag: flags[0] || null,
      isMarkedInRange,
      // Only the completed query's own cases: marked and overdue cases can also
      // be complete, but with their last update outside the range.
      isCompletedInRange: completedCaseRns.has(caseData.caseRn) && caseData.status === 'COMPLETE',
      overdueJob: caseJobs.find((job) => isJobOverdue(job, now)) || null,
      nextJob: caseJobs.find((job) => (getTime(job.executeAt) || 0) >= now) || null,
    };
  });
};

const emptyTypeSummary = (caseType) => ({
  caseType,
  open: 0,
  pending: 0,
  complete: 0,
  createdBeforeWindow: 0,
  stalled: 0,
  marked: 0,
  ...flagKeys.reduce((result, flagKey) => ({...result, [flagKey]: 0}), {}),
});

const addRowToSummary = (summary, row, rangeStartTime) => {
  if (row.status === 'OPEN') {
    summary.open += 1;
  }

  if (row.status === 'PENDING') {
    summary.pending += 1;
  }

  if (row.isCompletedInRange) {
    summary.complete += 1;
  }

  if (isOpenOrPending(row) && rangeStartTime !== null && (getTime(row.createdAt) || 0) < rangeStartTime) {
    summary.createdBeforeWindow += 1;
  }

  if (row.isMarkedInRange) {
    summary.marked += 1;
  }

  if (row.primaryFlag) {
    summary[row.primaryFlag] += 1;
  }

  if (stalledFlagKeys.includes(row.primaryFlag)) {
    summary.stalled += 1;
  }
};

// One summary per case type, busiest first, plus the all-types total.
export const buildTypeSummaries = (rows, rangeStartTime) => {
  const summariesByType = {};
  const total = emptyTypeSummary(null);

  rows.forEach((row) => {
    const caseType = row.caseType || '';

    if (!summariesByType[caseType]) {
      summariesByType[caseType] = emptyTypeSummary(caseType);
    }

    addRowToSummary(summariesByType[caseType], row, rangeStartTime);
    addRowToSummary(total, row, rangeStartTime);
  });

  const typeSummaries = Object.values(summariesByType).sort((firstSummary, secondSummary) => {
    const volumeDifference = (secondSummary.open + secondSummary.pending) - (firstSummary.open + firstSummary.pending);

    return volumeDifference || firstSummary.caseType.localeCompare(secondSummary.caseType);
  });

  return {typeSummaries, total};
};

const getCasePriority = (caseData) => {
  if (caseData.priority === null || caseData.priority === undefined || caseData.priority === '') {
    return Number.MAX_SAFE_INTEGER;
  }

  const priority = Number(caseData.priority);

  return Number.isFinite(priority) ? priority : Number.MAX_SAFE_INTEGER;
};

const sortGroupRows = (rows) => {
  return [...rows].sort((firstRow, secondRow) => {
    const priorityDifference = getCasePriority(firstRow) - getCasePriority(secondRow);

    return priorityDifference || (getTime(firstRow.createdAt) || 0) - (getTime(secondRow.createdAt) || 0);
  });
};

// Exceptions come first, followed by completed cases and pending cases without
// flags. Each case is listed once, with priority first, then oldest first.
export const buildFlagGroups = (rows, {includePending = true} = {}) => {
  const flagGroups = flagKeys.map((flagKey) => ({
    flagKey,
    rows: sortGroupRows(rows.filter((row) => row.primaryFlag === flagKey)),
  }));

  return [
    ...flagGroups,
    {
      flagKey: COMPLETED_GROUP_KEY,
      rows: sortGroupRows(rows.filter((row) => !row.primaryFlag && row.isCompletedInRange)),
    },
    {
      flagKey: 'PENDING',
      rows: includePending ? sortGroupRows(rows.filter((row) => row.status === 'PENDING' && !row.primaryFlag)) : [],
    },
  ];
};

export const getStatusLabel = (status) => {
  const statusLabel = translate(`case.Status.${status}.ColumnTitle`);

  return statusLabel === 'Missing translation' ? status : statusLabel;
};

// The largest unit only: 2d, 5h, 12m.
export const formatCompactDuration = (durationMs) => {
  if (durationMs >= DAY_MS) {
    return `${Math.floor(durationMs / DAY_MS)}d`;
  }

  if (durationMs >= HOUR_MS) {
    return `${Math.floor(durationMs / HOUR_MS)}h`;
  }

  return `${Math.max(1, Math.floor(durationMs / MINUTE_MS))}m`;
};

// Two units: 6d 2h, 2h 15m.
export const formatOpenFor = (durationMs) => {
  if (durationMs >= DAY_MS) {
    return `${Math.floor(durationMs / DAY_MS)}d ${Math.floor((durationMs % DAY_MS) / HOUR_MS)}h`;
  }

  if (durationMs >= HOUR_MS) {
    return `${Math.floor(durationMs / HOUR_MS)}h ${Math.floor((durationMs % HOUR_MS) / MINUTE_MS)}m`;
  }

  return `${Math.max(1, Math.floor(durationMs / MINUTE_MS))}m`;
};

// A job due today shows its time alone; any other day also shows the date.
export const formatJobTime = (value, now) => {
  const time = getTime(value);

  if (time === null) {
    return '';
  }

  if (new Date(time).toDateString() !== new Date(now).toDateString()) {
    return formatDateTime(time);
  }

  return new Date(time).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const getEarliestJob = (jobs) => {
  return jobs.reduce((earliestJob, job) => {
    const executeAt = getTime(job?.executeAt);

    return executeAt !== null && (!earliestJob || executeAt < getTime(earliestJob.executeAt)) ? job : earliestJob;
  }, null);
};

export const formatDateTime = (value) => {
  const time = getTime(value);

  if (time === null) {
    return '';
  }

  return new Date(time).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const formatMetricDate = (metricDate) => {
  const metricMoment = moment(metricDate, 'YYYY-MM-DD', true);

  return metricMoment.isValid() ? metricMoment.format('MMM D') : '';
};

export const getOpenForLabel = (row, now) => {
  const createdAt = getTime(row.createdAt);

  if (!isOpenOrPending(row) || createdAt === null) {
    return '';
  }

  return formatOpenFor(Math.max(0, now - createdAt));
};

export const getActivityLabel = (row, now) => {
  if (markFlagKeys.includes(row.primaryFlag) && row.markingMetricDate) {
    return translate('case.Monitor.Activity.Marked', {date: formatMetricDate(row.markingMetricDate)});
  }

  const createdAt = getTime(row.createdAt);
  const updatedAt = getTime(row.updatedAt);

  if (updatedAt === null || (createdAt !== null && updatedAt - createdAt < NO_ACTIVITY_TOLERANCE_MS)) {
    return translate('case.Monitor.Activity.NoneSinceCreated');
  }

  return translate('case.Monitor.Activity.Updated', {duration: formatCompactDuration(Math.max(0, now - updatedAt))});
};

const prettifyKey = (key) => {
  const label = `${key}`.replace(/_/g, ' ').toLowerCase();

  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Labels come from the case's guide rubric; a rubric edit can drop a category
// that older marks still carry, so the raw key is the fallback.
export const getFailureCategoryLabel = (row, domainGuidesByRn) => {
  if (!row.markingFailureCategory) {
    return translate('case.Monitor.DetailText.Uncategorized');
  }

  const rubricCategory = (domainGuidesByRn[row.domainGuideRn]?.rubric?.failureCategories || [])
      .find((category) => category.key === row.markingFailureCategory);

  return rubricCategory?.label || prettifyKey(row.markingFailureCategory);
};

export const getFlagDetailLabel = (row, now, domainGuidesByRn) => {
  const updatedAt = getTime(row.updatedAt || row.createdAt) || now;

  switch (row.primaryFlag) {
    case 'OVERDUE_JOB':
      return translate('case.Monitor.DetailText.JobDue', {
        duration: formatCompactDuration(Math.max(0, now - (getTime(row.overdueJob?.executeAt) || now))),
      });
    case 'STUCK_OPEN':
      return translate('case.Monitor.DetailText.NoChange', {duration: formatCompactDuration(Math.max(0, now - updatedAt))});
    case 'NOTHING_SCHEDULED':
      return translate('case.Monitor.DetailText.NoActivity', {duration: formatCompactDuration(Math.max(0, now - updatedAt))});
    case 'MARKED_BAD':
      return getFailureCategoryLabel(row, domainGuidesByRn);
    case 'HUMAN_INTERVENTION':
    case 'FLAGGED':
      return row.markingMarkerType ?
        translate(`case.Monitor.DetailText.MarkedBy.${row.markingMarkerType}`) :
        '';
    default:
      return '';
  }
};

// Participant RNs can carry a channel suffix (RN:CAREGIVER-V1-x:SUPPORT-V1-1) as
// well as a relationship one. The person is the leading RN, so that is what is
// looked up and named.
const getPersonRn = (participantRn) => {
  const personMatch = /^RN:[A-Z0-9_]+-V\d+-[^:]+/.exec(participantRn || '');

  return personMatch ? personMatch[0] : getParticipantResourceRn(participantRn);
};

// A participant that is not a client or caregiver has no name lookup, so it is
// shown by the kind its RN names: RN:STRIPE_CUSTOMER-V1-... reads "Stripe customer".
const getParticipantKindLabel = (personRn) => {
  const kindMatch = /^RN:([A-Z0-9_]+)-V\d+-/.exec(personRn || '');

  return kindMatch ? prettifyKey(kindMatch[1]) : translate('case.Communication.ParticipantTypes.OTHER');
};

export const getParticipantSummary = (row, participantDetails) => {
  const seenPersonRns = new Set();

  return getParticipantRns(row).reduce((result, participantRn) => {
    const personRn = getPersonRn(participantRn);

    if (!personRn || seenPersonRns.has(personRn)) {
      return result;
    }

    seenPersonRns.add(personRn);

    const participantKind = getParticipantKind(personRn);

    if (participantKind === 'CAREGIVER') {
      result.caregivers.push(getParticipantName(participantDetails, personRn));
    } else if (participantKind === 'CLIENT') {
      result.clients.push(getParticipantName(participantDetails, personRn));
    } else {
      result.others.push({participantRn: personRn, label: getParticipantKindLabel(personRn)});
    }

    return result;
  }, {caregivers: [], clients: [], others: []});
};

// Each person once, in case order, with the kind label shown before the name.
export const getParticipantList = (row, participantDetails) => {
  const seenPersonRns = new Set();

  return getParticipantRns(row).reduce((result, participantRn) => {
    const personRn = getPersonRn(participantRn);

    if (!personRn || seenPersonRns.has(personRn)) {
      return result;
    }

    seenPersonRns.add(personRn);

    const participantKind = getParticipantKind(personRn);
    const isNameable = ['CLIENT', 'CAREGIVER'].includes(participantKind);

    return [
      ...result,
      {
        personRn,
        kindLabel: isNameable ?
          translate(`case.Communication.ParticipantTypes.${participantKind}`) :
          getParticipantKindLabel(personRn),
        name: isNameable ? getParticipantName(participantDetails, personRn) : '',
      },
    ];
  }, []);
};

export const getNameableResourceRns = (rows) => {
  return [...new Set(rows.flatMap((row) => getParticipantRns(row))
      .map((participantRn) => getPersonRn(participantRn))
      .filter((personRn) => ['CLIENT', 'CAREGIVER'].includes(getParticipantKind(personRn))))];
};
